export const PACKAGE_PLACEHOLDERS = [
  { key: "contractor_name", label: "Contractor name" },
  { key: "company_name", label: "Company name" },
  { key: "signing_link", label: "Signing link" },
];

export const EMAIL_PLACEHOLDERS = [
  { key: "recipient_name", label: "Recipient name" },
  { key: "recipient_email", label: "Recipient email" },
  { key: "current_date", label: "Current date" },
  ...PACKAGE_PLACEHOLDERS,
];

const KNOWN_KEYS = new Set(EMAIL_PLACEHOLDERS.map((p) => p.key));
export const PACKAGE_SPECIFIC_KEYS = new Set(
  PACKAGE_PLACEHOLDERS.map((p) => p.key),
);

export const TEMPLATE_SCOPES = {
  GLOBAL: "global",
  CONTRACTOR: "contractor",
};

// Placeholders whose value differs per recipient (forces one email per "To" address)
export const RECIPIENT_SPECIFIC_KEYS = new Set([
  "recipient_name",
  "recipient_email",
]);

// Tolerates whitespace: {{recipient_name}} and {{ recipient_name }}
const PLACEHOLDER_REGEX = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

export const toToken = (key) => `{{${key}}}`;

export const extractPlaceholders = (...texts) => {
  const found = new Set();
  texts.forEach((text) => {
    for (const match of (text || "").matchAll(PLACEHOLDER_REGEX)) {
      found.add(match[1]);
    }
  });
  return [...found];
};

export const findUnknownPlaceholders = (...texts) =>
  extractPlaceholders(...texts).filter((key) => !KNOWN_KEYS.has(key));

// Values are keyed by placeholder name, not raw permit-package field names.
// An absent package passes an empty map; only placeholders actually used are checked.
export const findMissingPackagePlaceholders = (
  placeholderValues = {},
  ...texts
) =>
  extractPlaceholders(...texts).filter((key) => {
    if (!PACKAGE_SPECIFIC_KEYS.has(key)) return false;
    const value = placeholderValues?.[key];
    return typeof value !== "string" || !value.trim();
  });

const normalizeContractorId = (id) =>
  typeof id === "string" || typeof id === "number" ? String(id).trim() : "";

// Existing templates without scope or a contractor association remain global.
export const getTemplateScope = (template) =>
  template?.scope ??
  (normalizeContractorId(template?.contractorId)
    ? TEMPLATE_SCOPES.CONTRACTOR
    : TEMPLATE_SCOPES.GLOBAL);

// Used by the compose picker only; template management still shows all templates.
// No contractor context means global templates only. Unknown scopes fail closed.
export const isTemplateAvailableForContractor = (template, contractorId) => {
  if (!template) return false;
  const scope = getTemplateScope(template);
  if (scope === TEMPLATE_SCOPES.GLOBAL) return true;
  if (scope !== TEMPLATE_SCOPES.CONTRACTOR) return false;

  const selectedContractorId = normalizeContractorId(contractorId);
  return (
    Boolean(selectedContractorId) &&
    normalizeContractorId(template.contractorId) === selectedContractorId
  );
};

export const isBodyEmpty = (html) =>
  !html || !html.replace(/<[^>]*>/g, "").trim();
